// Таблицата „Движения" на складовата карта: Дата · Документ · Приход · Разход ·
// Наличност · Цена · Въвел, отгоре „Начално салдо", отдолу „Крайно салдо".
// Наличността не се показва под 0: редът, който я вкарва под нулата, носи
// червена бележка „⚠ 10 кг продадени без наличност", а приходът, който я покрива
// — „(10 покриват продаденото без наличност)"; салдо под нулата е 0 с бележка.
// Скролва се САМА (заглавието е залепено), затова дълъг период не разпъва
// страницата. Колоните следват ширината на КАРТАТА (container query), не на
// прозореца: под 640 px „Цена" отпада, а „Въвел" слиза под документа; под
// 460 px (телефон) Приход и Разход са една колона със знак.
import { useTranslation } from "react-i18next";
import type { StockCard, StockCardEntry } from "../../../../api/inventoryStockCard";
import { formatQty } from "../../components/qty";
import { formatUnitPrice } from "../../components/numbers";
import { shownStock } from "../../components/negativeStock";
import { fmtDateTime } from "../../components/dateTime";
import { ROW_TOTAL } from "../../components/list/density";
import { REPORT_HEAD_CLASS, REPORT_HEADER_ROW_CLASS, REPORT_ROW_CLASS } from "../components/reportListModel";
import { formatCardDate, isNegativeBalance, isOpenableDocument } from "./stockCardModel";

// Литерални класове — Tailwind сканира само пълни низове
const ROW_GRID =
  "grid grid-cols-[44px_minmax(0,1fr)_76px_80px] items-center gap-x-2.5 px-4 @min-[460px]:grid-cols-[50px_minmax(0,1fr)_70px_70px_82px] @min-[640px]:grid-cols-[54px_minmax(0,1fr)_76px_76px_88px_64px_minmax(64px,104px)]";
const PHONE_ONLY = "@min-[460px]:hidden";
const FROM_MEDIUM = "hidden @min-[460px]:block";
const FROM_WIDE = "hidden @min-[640px]:block";
const LABEL_SPAN = "col-span-3 @min-[460px]:col-span-4";

type Props = {
  card: StockCard;
  entries: StockCardEntry[]; // след филтъра по вид
  decimals: number;
  dateFormat: string;
  openingLabel: string; // „Начално салдо · 01.09.2026"
  closingLabel: string;
  emptyText: string | null; // няма редове (в периода или от избрания вид)
  documentLabelOf: (entry: StockCardEntry) => string;
  noteOf: (entry: StockCardEntry) => string;
  // бележката за минуса на реда (червена — под нулата; сива — покрит минус)
  negativeNoteOf: (entry: StockCardEntry) => { text: string; danger: boolean } | null;
  // салдо под нулата → „⚠ 10 кг продадени без наличност"
  balanceNoteOf: (balance: number) => string | null;
  onOpenDocument: (entry: StockCardEntry) => void;
};

// Приход зелен с „+", разход червен с „−"; нулата е празна клетка
const Quantity = ({
  value,
  sign,
  decimals,
  className = "",
}: {
  value: number;
  sign: "+" | "−";
  decimals: number;
  className?: string;
}) => (
  <span
    className={`text-right font-sheet tabular-nums ${sign === "+" ? "text-ok-deep" : "text-danger-ink"} ${className}`}
  >
    {value ? `${sign}${formatQty(value, decimals)}` : ""}
  </span>
);

// Под нулата е 0 — кой документ е вкарал минуса казва бележката на реда му
const Balance = ({ value, decimals, strong = false }: { value: number; decimals: number; strong?: boolean }) => {
  const { t } = useTranslation();
  return (
    <span
      title={isNegativeBalance(value) ? t("inventoryPage.scNegativeHint") : undefined}
      className={`text-right font-sheet tabular-nums text-ink ${strong ? "font-bold" : "font-semibold"}`}
    >
      {formatQty(shownStock(value), decimals)}
    </span>
  );
};

export const StockCardMovementsTable = ({
  card,
  entries,
  decimals,
  dateFormat,
  openingLabel,
  closingLabel,
  emptyText,
  documentLabelOf,
  noteOf,
  negativeNoteOf,
  balanceNoteOf,
  onOpenDocument,
}: Props) => {
  const { t } = useTranslation();

  const summaryRow = (label: string, value: number, position: "top" | "bottom") => {
    const balanceNote = balanceNoteOf(value);
    return (
      <div className={`${ROW_GRID} ${ROW_TOTAL} bg-fill ${position === "bottom" ? "border-t border-shade-2" : ""}`}>
        <span className={`${LABEL_SPAN} flex min-w-0 flex-col gap-px`}>
          <span className="text-[12.5px] font-semibold leading-[normal] text-ink">{label}</span>
          {balanceNote && <span className="text-[11.5px] leading-[1.3] text-danger-ink">{balanceNote}</span>}
        </span>
        <Balance value={value} decimals={decimals} strong />
      </div>
    );
  };

  return (
    <div className="@container min-h-0 flex-1 overflow-auto">
      <div className={`${ROW_GRID} ${REPORT_HEADER_ROW_CLASS} sticky top-0 z-[1] bg-panel`}>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colDate")}</span>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colDocument")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${PHONE_ONLY} text-right`}>{t("inventoryPage.colQty")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${FROM_MEDIUM} text-right`}>{t("inventoryPage.scColIn")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${FROM_MEDIUM} text-right`}>{t("inventoryPage.scColOut")}</span>
        <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.colAvailable")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${FROM_WIDE} text-right`}>{t("inventoryPage.scColPrice")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${FROM_WIDE}`}>{t("inventoryPage.scColUser")}</span>
      </div>
      {summaryRow(openingLabel, card.openingBalance, "top")}
      {entries.map((entry, index) => {
        const note = noteOf(entry);
        const negativeNote = negativeNoteOf(entry);
        const openable = isOpenableDocument(entry.documentType);
        return (
          <div key={`${entry.movementId}-${index}`} className={`${ROW_GRID} ${REPORT_ROW_CLASS}`}>
            <span className="font-sheet text-xs text-body" title={fmtDateTime(entry.date)}>
              {formatCardDate(entry.date, dateFormat)}
            </span>
            {/* широка карта: бележката е приглушена на реда на документа (един ред) */}
            <span className="flex min-w-0 flex-col gap-px @min-[640px]:flex-row @min-[640px]:flex-wrap @min-[640px]:items-baseline @min-[640px]:gap-x-2">
              {openable ? (
                <button
                  type="button"
                  onClick={() => onOpenDocument(entry)}
                  className="min-w-0 cursor-pointer truncate border-none bg-transparent p-0 text-left text-[13px] leading-[1.3] text-ink underline decoration-shade-4 underline-offset-2 outline-none hover:text-brand-deep-ink hover:decoration-brand focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
                >
                  {documentLabelOf(entry)}
                </button>
              ) : (
                <span className="truncate text-[13px] leading-[1.3] text-ink">{documentLabelOf(entry)}</span>
              )}
              {(note || entry.createdByName) && (
                <span className="flex min-w-0 gap-1 text-[11.5px] leading-[1.3]">
                  {note && (
                    <span
                      className={`truncate ${entry.substitutionRole ? "text-brand-deep-ink" : "text-muted"}`}
                    >
                      {note}
                    </span>
                  )}
                  {entry.createdByName && (
                    <span className="shrink-0 truncate text-muted @min-[640px]:hidden">
                      {note ? "· " : ""}
                      {entry.createdByName}
                    </span>
                  )}
                </span>
              )}
              {negativeNote && (
                <span
                  className={`text-[11.5px] leading-[1.3] @min-[640px]:basis-full ${negativeNote.danger ? "font-semibold text-danger-ink" : "text-muted"}`}
                >
                  {negativeNote.text}
                </span>
              )}
            </span>
            {/* телефон: една колона със знак */}
            {entry.quantityOut ? (
              <Quantity value={entry.quantityOut} sign="−" decimals={decimals} className={PHONE_ONLY} />
            ) : (
              <Quantity value={entry.quantityIn} sign="+" decimals={decimals} className={PHONE_ONLY} />
            )}
            <Quantity value={entry.quantityIn} sign="+" decimals={decimals} className={FROM_MEDIUM} />
            <Quantity value={entry.quantityOut} sign="−" decimals={decimals} className={FROM_MEDIUM} />
            <Balance value={entry.balanceAfter} decimals={decimals} />
            <span className={`${FROM_WIDE} text-right font-sheet text-xs tabular-nums text-body`}>
              {entry.unitPrice ? formatUnitPrice(entry.unitPrice) : ""}
            </span>
            <span className={`${FROM_WIDE} truncate text-xs text-body`}>{entry.createdByName ?? ""}</span>
          </div>
        );
      })}
      {emptyText && <div className="px-4 py-6 text-center text-[13px] leading-[1.4] text-body">{emptyText}</div>}
      {summaryRow(closingLabel, card.closingBalance, "bottom")}
    </div>
  );
};

export default StockCardMovementsTable;
